import React, { CSSProperties, FC, RefCallback } from 'react';
import {
    BLUR_FILTER,
    BLUR_PATTERNS,
    MAGIC_REVEAL,
    MAGIC_REVEAL_SHAPE,
    isHalfBlurred
} from './blurPatterns';

/** Matches the blur halves, so the blur banner can tell whether any blur is on screen */
export const MOBILE_BLUR_SELECTOR = '[data-mobile-blur]';

const HALVES = [0, 1] as const;

interface IMobileBlurProps {
    blurType: SpApi.ScreenshotDeterrenceMobileStyle;
    /** True after a long press has swapped which half is blurred */
    isSwapped: boolean;
    /** Magic Reveal's blurred layer, which the ink trail masks */
    inkLayerRef?: RefCallback<HTMLElement>;
    /** Set in a preview's Magic Reveal demo: the touch marker that follows its pretend finger */
    demoFingerRef?: RefCallback<HTMLElement>;
}

/** A translucent fingertip, like a screen recording's touch indicator; useMagicReveal moves it */
const demoFingerStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.55), 0 2px 8px rgba(0, 0, 0, 0.25)',
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.2s ease'
};

/** Closes in around the fingertip while it holds, before the ink starts */
const demoHoldRingStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.55)',
    opacity: 0
};

const DemoFinger: FC<Pick<IMobileBlurProps, 'demoFingerRef'>> = ({ demoFingerRef }) =>
    demoFingerRef ? (
        <span ref={demoFingerRef} aria-hidden style={demoFingerStyle}>
            <span style={demoHoldRingStyle} />
        </span>
    ) : null;

/**
 * Each layer's blur shape and whether it's blurred: Magic Reveal is one layer, always blurred,
 * whose mask useMagicReveal draws; the other styles are two complementary halves.
 */
const layersOf = ({ blurType, isSwapped }: IMobileBlurProps) =>
    blurType === MAGIC_REVEAL
        ? [{ key: MAGIC_REVEAL, shape: MAGIC_REVEAL_SHAPE, isBlurred: true }]
        : HALVES.map((half) => ({
              key: half,
              shape: BLUR_PATTERNS[blurType][half],
              isBlurred: isHalfBlurred(half, isSwapped)
          }));

/** The ref goes only on Magic Reveal's layer; halves don't take ink */
const inkRefOf = ({ blurType, inkLayerRef }: IMobileBlurProps) =>
    blurType === MAGIC_REVEAL ? inkLayerRef : undefined;

const fitStyles: Record<'cover' | 'contain', CSSProperties> = {
    cover: {
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    // Sized like a letterboxed photo (capped at the frame, centered) rather than filling the
    // frame, so the blur shapes measure from the photo's own edges.
    contain: {
        top: '50%',
        left: '50%',
        maxWidth: '100%',
        maxHeight: '100%',
        transform: 'translate(-50%, -50%)'
    }
};

interface IMobileBlurImageProps extends IMobileBlurProps {
    src: string;
    /**
     * How the photo sits in its positioned parent: filling it, or letterboxed and centered.
     * Must match the photo the halves cover.
     */
    fit: 'cover' | 'contain';
    /** The photo's own CSS filter (e.g. grayscale), so the halves match it */
    filter?: string;
}

/**
 * Covers a photo with copies of it, blurred in the style's shapes (two complementary halves, or
 * Magic Reveal's one inked layer). Render it right after the photo, inside the same positioned
 * parent.
 */
export const MobileBlurImage: FC<IMobileBlurImageProps> = ({
    filter,
    fit,
    src,
    demoFingerRef,
    ...blur
}) => (
    <>
        {layersOf(blur).map(({ key, shape, isBlurred }) => (
            <img
                key={key}
                ref={inkRefOf(blur)}
                data-mobile-blur
                alt=""
                draggable={false}
                src={src}
                style={{
                    ...fitStyles[fit],
                    ...shape,
                    position: 'absolute',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    filter: [filter, isBlurred && BLUR_FILTER].filter(Boolean).join(' ') || 'none',
                    transition: 'filter 0.15s ease'
                }}
            />
        ))}
        <DemoFinger demoFingerRef={demoFingerRef} />
    </>
);

/**
 * Blurs whatever paints behind it (e.g. the slideshow canvas, which can't be copied like a
 * photo) in the style's shapes. Render it inside a positioned parent; it fills it.
 */
export const MobileBlurBackdrop: FC<IMobileBlurProps> = ({ demoFingerRef, ...blur }) => (
    <>
        {layersOf(blur).map(({ key, shape, isBlurred }) => {
            const backdropFilter = isBlurred ? BLUR_FILTER : 'none';

            return (
                <div
                    key={key}
                    ref={inkRefOf(blur)}
                    data-mobile-blur
                    style={{
                        ...shape,
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        // iOS Safari only composites (and blurs) a backdrop-filter layer when the
                        // element paints something, so give each half a near-invisible background.
                        backgroundColor: 'rgba(0, 0, 0, 0.001)',
                        backdropFilter,
                        WebkitBackdropFilter: backdropFilter,
                        transition: 'backdrop-filter 0.15s ease, -webkit-backdrop-filter 0.15s ease'
                    }}
                />
            );
        })}
        <DemoFinger demoFingerRef={demoFingerRef} />
    </>
);
